import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { useRef } from 'react';
import { useButton } from 'react-aria';

import { mergeRefs } from '../../lib/mergeRefs';
import { isMACOS } from '../../lib/platform';
import { Glyph } from '../common/Glyph';
import { PremiumLocked } from '../premium/PremiumLocked';

import { animationFade } from '../../theme/animations/animationFade';
import { su } from '../../theme/functions/su';
import { glyph } from '../../theme/glyph';
import { motion } from '../../theme/motion';
import { theme } from '../../theme/theme';

import { DeviceCapabilityList } from './DeviceCapabilityList';
import { DeviceIcon } from './DeviceIcon';

export function DeviceTile(props) {
  const ref = useRef();
  const instanceRef = useRef({
    time: null,
  });
  const isLargeTile = props.tileSize === DeviceTile.size.large;
  const isNoInteraction = props.isNoInteraction === true || props.isUnknown === true;
  const shouldRenderQuickAction = props.quickAction != null;
  const button = useButton(
    {
      onPressStart(event) {
        // console.log('onPressStart');
        instanceRef.current.time = Date.now();
      },
      onPress(event) {
        if (event.ctrlKey && isMACOS === true) return;

        // console.log('onPress');
        // const ms = Date.now() - (instanceRef.current.time ?? 0);

        // if (ms > 300 && props.onLongPress != null) {
        //   props.onLongPress(event);
        // } else {
        props.onPress?.(event);
        // }
      },
      onPressEnd(event) {
        // console.log('onPressEnd');
      },
      onPressUp(event) {
        // console.log('onPressUp');
      },
    },
    ref,
  );

  return (
    <DeviceTile.Root
      {...(props.isRenaming !== true && isNoInteraction !== true && button.buttonProps)}
      data-testid="device-tile"
      ref={mergeRefs([props.ref, ref])}
      title={props.isUnknown ? props.deviceId : props.title}
      data-is-drag-preview={props.isDragPreview === true}
      data-is-dragging={props.isDragging === true}
      data-is-locked={props.isLocked === true}
      data-is-no-interaction={isNoInteraction}
      data-is-off={props.isOn === false}
      data-is-selected={props.isSelected === true}
      data-is-unknown={props.isUnknown === true}
      data-size={props.tileSize === DeviceTile.size.large ? 'large' : 'medium'}
      onContextMenu={props.onContextMenu}
    >
      {props.isLocked === true ? <PremiumLocked zIndex={2} /> : null}

      <DeviceTile.Border data-is-unknown={props.isUnknown === true}>
        {props.isUnknown !== true ? (
          <DeviceTile.ShiftLock>
            <DeviceTile.Header>
              <DeviceTile.TopContainer>
                <DeviceIcon
                  iconObj={props.iconObj}
                  iconUrl={props.iconUrl}
                  iconOverride={props.iconOverride}
                  size={props.tileSize === DeviceTile.size.large ? theme.icon.size_xlarge : '40px'}
                />

                {shouldRenderQuickAction === true ? (
                  <DeviceTile.QuickAction>{props.quickAction}</DeviceTile.QuickAction>
                ) : null}
              </DeviceTile.TopContainer>

              <DeviceTile.BottomContainer>
                {props.children ? (
                  props.children
                ) : (
                  <DeviceTile.Name title={props.name}>{props.name}</DeviceTile.Name>
                )}

                {props.indicator ? <DeviceTile.Status>{props.indicator}</DeviceTile.Status> : null}
              </DeviceTile.BottomContainer>
            </DeviceTile.Header>

            {isLargeTile === true ? <DeviceTile.Ruler /> : null}

            {props.tileSize === DeviceTile.size.large && props.capabilityList}
          </DeviceTile.ShiftLock>
        ) : (
          <Glyph
            size={theme.icon.size_xlarge}
            color={theme.icon.color_body_light}
            source={glyph.core.exclamationmarkTriangleFill}
          />
        )}
      </DeviceTile.Border>
    </DeviceTile.Root>
  );
}

DeviceTile.size = {
  medium: 1,
  large: 2,
};

/**
 * Shiftlock's main purpose is to improve animation when you switch between normal and large device tiles.
 */
DeviceTile.ShiftLock = styled.div`
  position: absolute;
  top: -2px;
  left: -2px;
  display: flex;
  flex: 1 1 auto;
  width: var(--tile-size);
  height: var(--tile-size);
  padding: ${su(1)};
  flex-direction: column;
  border-radius: ${theme.borderRadius.default};
  transition: filter ${motion.duration.fast} ease-in-out;
`;

/**
 * Border
 * @description important div to make certain the active blue works together with different tile sizes
 * and the effects when switching between them. Also gives enough space to scrollable area's in large
 * device tiles so they scrollarea is until the edge of the tile
 */
DeviceTile.Border = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border: 2px solid transparent;
  transition: border ${motion.duration.fast} ease-in-out;
  border-radius: ${theme.borderRadius.default};

  &[data-is-unknown='true'] {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

DeviceTile.Header = styled.header`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
`;

DeviceTile.Ruler = styled.div``;

DeviceTile.QuickAction = styled.div`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: flex-start;
  justify-content: flex-end;
  margin-inline-start: auto;
`;

DeviceTile.Status = styled.div`
  display: inline-flex;
  align-items: center;
  height: 16px;
  min-height: 16px;
  color: ${theme.color.text_light};

  &:empty {
    display: none;
    height: 0;
    min-height: 0;
  }

  & > * {
    margin: 0 !important;
  }
`;

DeviceTile.TopContainer = styled.div`
  display: flex;
  flex: 1 1 auto;
  align-items: flex-start;
  justify-content: space-between;
`;

DeviceTile.BottomContainer = styled.div`
  display: flex;
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  align-items: flex-end;
  justify-content: flex-start;
  min-height: 48px;
  padding: ${su(1)};
  border-radius: 0 0 12px 12px;
`;

DeviceTile.Name = styled.div`
  max-height: 36px;
  overflow: hidden;
  font-size: ${theme.fontSize.small};
  line-height: 1.286;
  text-align: start;
  text-overflow: ellipsis;
  word-break: normal;
  white-space: pre-line;
  hyphens: auto;
`;

const fadeIn = css`
  opacity: 0;
  animation: ${animationFade.in} ${motion.duration.slow} ease-out 1 forwards;
`;

DeviceTile.Root = styled.div`
  --tile-size: ${theme.device.size_medium};
  --transform-transition: transform ease-in-out ${motion.duration.fast};
  --default-transition:
    width ease-in-out ${motion.duration.slow}, height ease-in-out ${motion.duration.slow},
    border ease-in-out ${motion.duration.fast}, var(--transform-transition);

  display: flex;
  position: relative;
  appearance: none;
  flex-direction: column;
  align-items: normal;
  width: var(--tile-size);
  height: var(--tile-size);
  padding: ${su(1)};
  will-change: transform;
  border-radius: ${theme.borderRadius.default};
  outline: 0;
  background-color: ${theme.color.component_on};
  box-shadow: ${theme.boxShadow.default};
  cursor: pointer;

  transition: var(--default-transition);
  overflow: hidden;

  &[data-is-off='true'] {
    --indicator-background-color: ${theme.color.component_off};
    background-color: ${theme.color.component_off};
  }

  &[data-is-selected='true'] {
    ${DeviceTile.Border} {
      border-color: ${theme.color.highlight};
    }
  }

  &[data-is-drag-preview='true'] {
    transform: scale(0.95);
    animation: ${keyframes`
      from {
        transform: scale(1);
      }

      to {
        transform: scale(0.95);
      }
    `} ${motion.duration.slow} ease-out forwards 1;
  }

  &[data-is-dragging='true'] {
    opacity: 0;
  }

  &[data-is-no-interaction='true'] {
    cursor: default;
  }

  &[data-is-unknown='true'] {
    background-color: rgba(221, 222, 226, 0.5);
    background-image: url("data:image/svg+xml,%3Csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='100%25' height='100%25' fill='none' rx='10' ry='10' stroke='rgb(151, 151, 151)' stroke-width='4' stroke-dasharray='10, 10' stroke-dashoffset='10' stroke-linecap='square'/%3E%3C/svg%3E");
  }

  &[data-is-locked='true'] {
    --indicator-background-color: ${theme.color.component_off};
    background-color: ${theme.color.component_off};

    ${DeviceTile.ShiftLock} {
      opacity: 0.7;
      filter: grayscale(1);
    }

    ${DeviceTile.Status} {
      opacity: 0;
    }
  }

  &[data-size='medium'] {
    ${DeviceTile.BottomContainer} {
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-end;
      bottom: ${su(1)};
      gap: 4px;
      min-height: 54px;
      padding: 0 ${su(1)};
    }

    ${DeviceTile.Name} {
      max-height: 36px;
    }

    ${DeviceTile.Status} {
      max-width: calc(var(--tile-size) - ${su(2)});
    }
  }

  &[data-size='large'] {
    --tile-size: ${theme.device.size_large};

    ${DeviceTile.ShiftLock} {
      align-items: stretch;
    }

    ${DeviceTile.Header} {
      flex: 0 0 auto;
      flex-direction: column;
      align-items: stretch;
      height: auto;
      margin-bottom: 0;
    }

    ${DeviceTile.TopContainer} {
      flex: 0 0 auto;
      width: 100%;
      min-height: 32px;
      margin-bottom: ${su(1)};
    }

    ${DeviceTile.Ruler} {
      position: relative;
      z-index: 1;

      &::before {
        position: absolute;
        content: '';
        z-index: 0;
        top: 50%;
        right: 0;
        left: 0;
        height: 1px;
        background: ${theme.color.component_line};
      }
    }

    ${DeviceTile.BottomContainer} {
      flex-direction: column;
      position: relative;
      flex: 0 0 auto;
      padding: 0;
      margin-inline-start: 0;
      margin-bottom: ${su(1)};
      min-height: 0;
      display: flex;
      align-items: flex-start;
      justify-content: flex-start;
      gap: 1px;
    }

    ${DeviceTile.QuickAction} {
      align-self: flex-start;
    }

    ${DeviceTile.Name} {
      font-weight: ${theme.fontWeight.medium};
      font-size: ${theme.fontSize.regular};
      line-height: 20px;
      display: block;
      width: 100%;
      max-height: 20px;
      padding: 0;
      margin: 0;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }

    ${DeviceTile.Status} {
      height: 20px;
      min-height: 20px;
      max-width: calc(var(--tile-size) - ${su(2)});
    }

    &[data-is-drag-preview='false'] {
      ${DeviceTile.Ruler},
      ${DeviceTile.Name},
      ${DeviceTile.Status} {
        ${fadeIn};
      }
    }

    &:hover {
      ${DeviceCapabilityList.Root} {
        margin-bottom: -11px;
      }
    }
  }
`;
